import { useRef, useReducer, useEffect } from 'react';
import Button from 'components/ui-components/Button';
import useOnScreen from 'hooks/useOnScreen';
import { DeleteIcon, ArchiveIcon, UnarchiveIcon } from 'assets/icons';

import ApprovalsGroupSelectedUsers from './approvals_group_selected_users';
import ApprovalsGroupFormInputs from './approvals_group_form_inputs';
import ApprovalsGroupMessageModal from './approvals_group_message_modal';
import ApprovalsGroupSelectModal from './approvals_group_select_modal';
import ApprovalsGroupArchived from './approvals_group_archived';

import useInititalLoadRequest from './useInitialLoadRequest';
import useSelectUserModal from './useSelectUserModal';
import useSubmitGroupRequest from './useSubmitGroupRequest';
import useGroupStatusRequest from './useGroupStatusRequest';

import {
  requestErrors,
  inputPrompts,
  approvalsGroupText,
} from './approvals_group_text';
import {
  initialGroupState,
  approvalsGroupReducer,
  groupActions,
  requestTypes,
} from './approvals_group_reducer';
import {
  initialMessageModalState,
  messageModalReducer,
  messageModalActions,
  messageModalTypes,
} from '../groups_listing/approvals_groups_reducer';

const ApprovalsGroupForm = ({
  editMode,
  initialLoadUrl,
  editGroupUrl,
  getUsersUrl,
  validateNameUrl,
  groupsUrl,
}) => {
  const [approvalGroupState, approvalGroupDispatch] = useReducer(
    approvalsGroupReducer,
    initialGroupState,
  );

  const [messageModalState, messageModalDispatch] = useReducer(
    messageModalReducer,
    initialMessageModalState,
  );

  const {
    initialGroupName,
    formUpdated,
    nameInput,
    nameMaxLength,
    nameIsValid,
    nameErrMsg,
    descriptionInput,
    descriptionMaxLength,
    admins,
    adminsCount,
    adminsSelectionCount,
    adminsIsValid,
    adminsErrMsg,
    approvers,
    approversCount,
    approversSelectionCount,
    approversIsValid,
    users,
    usersCount,
    usersSelectionCount,
    usersIsValid,
    selectUsersModal,
    removeUserModal,
    groupStatus,
    routes,
    request,
  } = approvalGroupState;

  const { formRedirectUrl, changeStatusUrl, createGroupUrl } = routes;

  // --- INITIAL LOAD REQUEST --- //

  useInititalLoadRequest(
    editMode,
    initialLoadUrl,
    editGroupUrl,
    request,
    approvalGroupDispatch,
  );

  // --- GROUP STATUS REQUEST --- //
  useGroupStatusRequest(
    editMode,
    groupStatus,
    request,
    approvalGroupDispatch,
    editGroupUrl,
    changeStatusUrl,
    groupsUrl,
  );
  // ---- MODAL - GROUP STATUS ----- //
  const openModalHandler = type => {
    let modalType = '';

    const { status, canDelete } = groupStatus;

    switch (type) {
      case messageModalTypes.ARCHIVE:
        if (status === 'active') {
          modalType = messageModalTypes.ARCHIVE;
        }
        break;
      case messageModalTypes.UNARCHIVE:
        if (status === 'archived') {
          modalType = messageModalTypes.UNARCHIVE;
        }
        break;
      case messageModalTypes.DELETE:
        if (status === 'active' && !canDelete) {
          modalType = messageModalTypes.DELETE_ACTIVE_GROUP;
        } else if (status === 'archived' && !canDelete) {
          modalType = messageModalTypes.DELETE_ARCHIVE_GROUP;
        } else {
          modalType = messageModalTypes.DELETE;
        }
        break;
      default:
        return;
    }
    messageModalDispatch({
      type: `OPEN_${modalType}_MESSAGE_MODAL`,
      payload: {
        group: {
          name: initialGroupName,
          delete_approval_group_url: editGroupUrl,
          change_approval_group_status_url: changeStatusUrl,
        },
      },
    });
  };
  const modalActionHandler = () => {
    let type = '';
    switch (messageModalState.type) {
      case messageModalTypes.DELETE:
        type = requestTypes.DELETE_GROUP;
        break;
      case messageModalTypes.DELETE_ACTIVE_GROUP:
      case messageModalTypes.ARCHIVE:
      case messageModalTypes.UNARCHIVE:
        type = requestTypes.GROUP_STATUS;
        break;
      default:
        break;
    }
    approvalGroupDispatch({
      type: groupActions.ENABLE_REQUEST,
      payload: { type, params: '' },
    });
    messageModalDispatch({ type: messageModalActions.CLOSE_MESSAGE_MODAL });
  };
  const closeMessageModal = () => {
    messageModalDispatch({ type: messageModalActions.CLOSE_MESSAGE_MODAL });
  };

  // ---- MODAL - SELECT USERS ----- //

  const { openUsersModal, closeUsersModal } = useSelectUserModal(
    admins,
    approvers,
    users,
    adminsCount,
    approversCount,
    usersCount,
    adminsSelectionCount,
    approversSelectionCount,
    usersSelectionCount,
    approvalsGroupText,
    request,
    getUsersUrl,
    approvalGroupDispatch,
    groupActions,
  );

  // ---- MODAL - REMOVE USERS ----- //

  const openUserMessageModal = (user, title) => {
    approvalGroupDispatch({
      type: groupActions.OPEN_MESSAGE_MODAL,
      payload: { user, title },
    });
  };

  const closeUserMessageModal = () => {
    approvalGroupDispatch({ type: groupActions.CLOSE_MESSAGE_MODAL });
  };

  const removeUserHandler = () => {
    approvalGroupDispatch({ type: groupActions.REMOVE_MESSAGE_MODAL });
    approvalGroupDispatch({ type: groupActions.FORM_UPDATED });
  };

  // ---- CHECK ERRORS - ONSCREEN ---- //

  const nameErrRef = useRef();
  const nameErrVisible = useOnScreen(nameErrRef);
  const adminErrRef = useRef();
  const adminErrVisible = useOnScreen(adminErrRef);
  const formRef = useRef();

  // ------ SUBMIT GROUP REQUEST ------ //

  useSubmitGroupRequest(
    nameInput,
    descriptionInput,
    admins,
    approvers,
    users,
    editMode,
    createGroupUrl,
    editGroupUrl,
    request,
    approvalGroupDispatch,
  );

  // -------- SUBMIT FORM -------- //

  const submitFormHandler = event => {
    event.preventDefault();

    const noGroupName = !nameInput.trim().length;

    // Prevent form submission if name or amdin are empty/invalid
    if (
      noGroupName ||
      !adminsSelectionCount ||
      !nameIsValid ||
      !adminsIsValid
    ) {
      // If group name is empty set error
      if (noGroupName)
        approvalGroupDispatch({
          type: groupActions.NAME_INVALID,
          payload: inputPrompts.uniqueName,
        });
      // If admin is not selected set error
      if (!adminsSelectionCount)
        approvalGroupDispatch({
          type: groupActions.ADMINS_INVALID,
          payload: inputPrompts.addGroupAdmin,
        });
      // Scroll group name into view
      if ((noGroupName || !nameIsValid) && !nameErrVisible) {
        formRef.current.scrollIntoView();
        return;
      }
      // Scoll admin selection into view
      if ((!adminsSelectionCount || !adminsIsValid) && !adminErrVisible) {
        formRef.current.scrollIntoView();
      }
      return;
    }

    // Enable HTTP Request to be sent
    approvalGroupDispatch({
      type: groupActions.ENABLE_REQUEST,
      payload: { type: requestTypes.SUBMIT_GROUP, params: '' },
    });
  };

  const cancelFormHandler = () => {
    window.location.assign(groupsUrl);
  };

  // ---- BEFORE UNLOAD ---- //
  // The following shows an alert if a user has entered
  // data into the form and tries to leave or refesh the page.

  const beforeUnloadListener = event => {
    event.preventDefault();
    event.returnValue = 'Changes that you made will not be saved.';
  };

  useEffect(() => {
    if (formUpdated) {
      // Add event listener if any of the fields have been updated
      window.addEventListener('beforeunload', beforeUnloadListener);
    }

    if (formRedirectUrl) {
      // Remove unload event listener to prevent pop up on redirect
      window.removeEventListener('beforeunload', beforeUnloadListener);
      // Redirect to group listing page after successful group creation/update
      window.location.assign(formRedirectUrl);
    }

    return () => {
      // Remove event listener when component unmounts
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [formUpdated, formRedirectUrl]);

  return (
    <>
      {groupStatus.status === 'archived' && (
        <ApprovalsGroupArchived
          groupStatus={groupStatus}
          messaging={groupStatus.messaging}
          openModalHandler={openModalHandler}
        />
      )}
      <form
        onSubmit={submitFormHandler}
        className="create-group-form mt-20"
        ref={formRef}>
        <h2 className="ff-body fw-600 fs-18 tc-grey-70 lh-1 mb-12">
          Group Details
        </h2>

        <ApprovalsGroupFormInputs
          initialGroupName={initialGroupName}
          nameInput={nameInput}
          nameIsValid={nameIsValid}
          nameErrMsg={nameErrMsg}
          nameErrRef={nameErrRef}
          descriptionInput={descriptionInput}
          approvalGroupDispatch={approvalGroupDispatch}
          validateNameUrl={validateNameUrl}
          nameMaxLength={nameMaxLength}
          descriptionMaxLength={descriptionMaxLength}
        />

        <hr
          className="create-group-divider mb-25"
          tabIndex="-1"
          aria-hidden="true"
        />

        <h2 className="ff-body fw-600 fs-18 tc-grey-70 lh-1 mb-20">Users</h2>

        <ApprovalsGroupSelectedUsers
          displayText={approvalsGroupText.admins}
          userCount={adminsCount}
          isAdmin
          users={admins}
          isValid={adminsIsValid}
          errorMsg={adminsErrMsg}
          errorRef={adminErrRef}
          openUsersModal={openUsersModal}
          openUserMessageModal={openUserMessageModal}
          classes="mb-32"
        />

        <ApprovalsGroupSelectedUsers
          displayText={approvalsGroupText.approvers}
          userCount={approversCount}
          users={approvers}
          isValid={approversIsValid}
          errorMsg={requestErrors.error}
          openUsersModal={openUsersModal}
          openUserMessageModal={openUserMessageModal}
          classes="mb-32"
        />

        <ApprovalsGroupSelectedUsers
          displayText={approvalsGroupText.users}
          userCount={usersCount + approversCount}
          users={users}
          isValid={usersIsValid}
          errorMsg={requestErrors.error}
          openUsersModal={openUsersModal}
          openUserMessageModal={openUserMessageModal}
          classes="mb-60"
        />
        <hr className="create-group-divider" tabIndex="-1" aria-hidden="true" />
        <div className="fl jc-sb">
          <div className="fl ai-c">
            <Button type="submit" className="bg-cef-red tc-white ptb-12 plr-14">
              {editMode
                ? approvalsGroupText.btns.update
                : approvalsGroupText.btns.submit}
            </Button>
            <Button
              handleClick={cancelFormHandler}
              className="bg-transparent tc-grey-70 ml-16 p-0">
              {approvalsGroupText.btns.cancel}
            </Button>
          </div>
          {editMode && (
            <div className="fl">
              <Button
                className="bg-transparent tc-grey-70 p-0"
                handleClick={() =>
                  openModalHandler(
                    groupStatus.status === 'active'
                      ? messageModalTypes.ARCHIVE
                      : messageModalTypes.UNARCHIVE,
                  )
                }
                aria-label={
                  groupStatus.status === 'active' ? 'Archive' : 'Unarchive'
                }>
                {groupStatus.status === 'active' ? (
                  <ArchiveIcon />
                ) : (
                  <UnarchiveIcon />
                )}
                <span className="ml-6">
                  {groupStatus.status === 'active' ? 'Archive' : 'Unarchive'}
                </span>
              </Button>
              <Button
                className="bg-transparent tc-grey-70 p-0 ml-20"
                handleClick={() => openModalHandler(messageModalTypes.DELETE)}
                aria-label="Delete">
                <DeleteIcon />
                <span className="ml-6">Delete</span>
              </Button>
            </div>
          )}
        </div>
      </form>
      <ApprovalsGroupSelectModal
        options={selectUsersModal}
        closeModal={closeUsersModal}
        approvalGroupDispatch={approvalGroupDispatch}
      />
      {/* Modal Used to Remove Selected Users from Group */}
      <ApprovalsGroupMessageModal
        options={removeUserModal}
        closeModal={closeUserMessageModal}
        actionHandler={removeUserHandler}
      />
      {/* Modal Used to Archive/Delete Group */}
      <ApprovalsGroupMessageModal
        options={messageModalState}
        closeModal={closeMessageModal}
        actionHandler={modalActionHandler}
      />
    </>
  );
};

export default ApprovalsGroupForm;
